import React, { useContext, useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  FormControl,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import * as Yup from "yup";
import SkeletonWrapper from "../../../../components/Skeleton/SkeletonWrapper";
import { CustomTextField } from "../../../../components/CustomFields/TextField";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { PreferencesContext } from "../../../../PreferenceContext";
import {
  SaveMemberPatientReportRangeValues,
  UpdateMemberPatientReportRangeValues,
} from "../../../../services/ReportParameterService";
import { AlertType } from "../../../../services/models/AlertType";

interface formInitialValueTypes {
  memberPatientReportParameterForRangeValueId: number;
  opdId: number;
  patientId: number;
  investigationId: number;
  investigationParameterId: number;
  optionValue: string;
}

const ReportParameterForRange = (props: any) => {
  // PROPS
  const {
    isForPopUp = false,
    handleClosePopUp,
    opd,
    investigationId,
    investigationParameterId,
    optionValue,
    memberPatientReportParameterForRangeValueId,
  } = props;

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { userContext, updateUserContext } = useContext(PreferencesContext);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    memberPatientReportParameterForRangeValueId: Yup.number().nullable(),
    optionValue: Yup.string().required("Option value is required"),
  });

  const initRangeData: formInitialValueTypes = {
    memberPatientReportParameterForRangeValueId: 0,
    opdId: 0,
    patientId: 0,
    investigationId: 0,
    investigationParameterId: 0,
    optionValue: "",
  };

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initRangeData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  const saveRangeValue = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";

      const formData = {
        MemberPatientReportParameterForRangeValueId:
          value?.memberPatientReportParameterForRangeValueId || 0,
        opdId: opd?.opdId || 0,
        patientId: opd?.patientId || 0,
        memberId: 0,
        investigationId: investigationId || 0,
        investigationParameterId: investigationParameterId || 0,
        optionValue: value?.optionValue || "",
        rowManagedBy: userContext?.loginDetail?.userId,
      };

      if (value?.memberPatientReportParameterForRangeValueId > 0) {
        result = await UpdateMemberPatientReportRangeValues(formData);
      } else {
        result = await SaveMemberPatientReportRangeValues(formData);
      }

      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          handleClosePopUp?.("REPORT_PARAMETER", result.data);
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message,
        alertType: AlertType.Error,
      });
    }
  };
  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveRangeValue(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (!isForPopUp) {
      navigate(-1);
    } else {
      handleClosePopUp("CLOSE");
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      methods?.reset({
        memberPatientReportParameterForRangeValueId:
          memberPatientReportParameterForRangeValueId || 0,
        opdId: opd?.opdId || 0,
        patientId: opd?.patientId || 0,
        investigationId: investigationId || 0,
        investigationParameterId: investigationParameterId || 0,
        optionValue: optionValue || "",
      });
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [
    investigationParameterId,
    optionValue,
    memberPatientReportParameterForRangeValueId,
  ]);

  return (
    <Box>
      <Box>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} mb={3}>
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12} sm={12} md={12}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.optionValue
                            ? "validation-message"
                            : ""
                        }`}>
                        Option *
                      </FormLabel>
                      <TextField
                        placeholder=""
                        type="text"
                        fullWidth
                        size="small"
                        autoFocus
                        {...methods.register("optionValue", {
                          required: true,
                        })}
                        onChange={(e) => {
                          methods.setValue(
                            "optionValue",
                            e.target.value.toUpperCase(),
                          );
                        }}
                        error={!!methods.formState.errors.optionValue}
                        className={`text-input ${
                          methods.formState.errors.optionValue ? "error" : ""
                        }`}
                      />
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.optionValue?.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={isLoading || methods.formState.isSubmitting}>
                    Save
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    disabled={isLoading || methods.formState.isSubmitting}
                    onClick={handleBack}>
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
};

export default ReportParameterForRange;
